import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { apiErrorMessage } from '../lib/apiErrors';
import Layout from '../components/Layout';

const VAZIO = { codigo: '', nome: '', razao_social: '', cnpj: '', contrato: '', vigencia_inicio: '', vigencia_fim: '', ativo: true, cor: '#0ea5e9', observacoes: '' };

export default function Convenios() {
  const { api, can } = useAuth();
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    try { setLista((await api.get('/financeiro/convenios')).data); } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    setSalvando(true);
    try {
      const body = { ...edit }; delete body.id; delete body.internacoes_vinculadas;
      ['razao_social', 'cnpj', 'contrato', 'vigencia_inicio', 'vigencia_fim', 'observacoes'].forEach((k) => { if (!body[k]) body[k] = null; });
      if (edit.id) await api.put(`/financeiro/convenios/${edit.id}`, body); else await api.post('/financeiro/convenios', body);
      toast.success(edit.id ? 'Convênio atualizado' : 'Convênio cadastrado');
      setEdit(null); carregar();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSalvando(false); }
  };

  const excluir = async (c) => {
    if (!window.confirm(`Excluir o convênio ${c.nome}? (só é possível sem vínculos)`)) return;
    try { await api.delete(`/financeiro/convenios/${c.id}`); toast.success('Convênio excluído'); carregar(); } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const campo = (k, label, props = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`conv-${k}`}>{label}</Label>
      <Input id={`conv-${k}`} value={edit?.[k] ?? ''} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`conv-input-${k}`} {...props} />
    </div>
  );

  return (
    <Layout>
    <div className="space-y-6" data-testid="convenios-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gestão de Convênios</h1>
          <p className="text-muted-foreground">Convênios identificam o pagador de cada episódio de internação</p>
        </div>
        {can('financeiro.convenios.cadastrar') && (
          <Button onClick={() => setEdit({ ...VAZIO })} data-testid="btn-novo-convenio"><Plus className="h-4 w-4 mr-2" />Novo Convênio</Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="convenios-table">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr><th className="p-3">Código</th><th className="p-3">Nome</th><th className="p-3">CNPJ</th><th className="p-3">Contrato</th><th className="p-3">Vigência</th><th className="p-3">Internações</th><th className="p-3">Situação</th><th className="p-3 text-right">Ações</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Carregando…</td></tr>}
              {!loading && lista.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground" data-testid="convenios-empty"><Building2 className="h-8 w-8 mx-auto mb-2 opacity-40" />Nenhum convênio cadastrado</td></tr>}
              {lista.map((c) => (
                <tr key={c.id} className="border-t" data-testid={`convenio-row-${c.codigo}`}>
                  <td className="p-3 font-mono"><span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded-full inline-block" style={{ backgroundColor: c.cor }} />{c.codigo}</span></td>
                  <td className="p-3 font-medium">{c.nome}{c.razao_social && <div className="text-xs text-muted-foreground">{c.razao_social}</div>}</td>
                  <td className="p-3">{c.cnpj || '—'}</td>
                  <td className="p-3">{c.contrato || '—'}</td>
                  <td className="p-3 text-xs">{c.vigencia_inicio || '—'}{c.vigencia_fim ? ` → ${c.vigencia_fim}` : ''}</td>
                  <td className="p-3">{c.internacoes_vinculadas}</td>
                  <td className="p-3"><Badge variant="outline" className={c.ativo ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-500'} data-testid={`convenio-status-${c.codigo}`}>{c.ativo ? 'Ativo' : 'Inativo'}</Badge></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {can('financeiro.convenios.editar') && (<>
                      <Button variant="ghost" size="icon" onClick={() => setEdit({ ...VAZIO, ...c })} data-testid={`btn-editar-convenio-${c.codigo}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => excluir(c)} disabled={c.internacoes_vinculadas > 0} data-testid={`btn-excluir-convenio-${c.codigo}`}><Trash2 className="h-4 w-4" /></Button>
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl" data-testid="convenio-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? 'Editar Convênio' : 'Novo Convênio'}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {campo('codigo', 'Código interno *', { placeholder: 'UNIMED', maxLength: 20 })}
              {campo('nome', 'Nome do convênio *', { placeholder: 'UNIMED' })}
              {campo('razao_social', 'Razão social')}
              {campo('cnpj', 'CNPJ', { placeholder: '00.000.000/0000-00' })}
              {campo('contrato', 'Número do contrato')}
              <div className="space-y-1.5">
                <Label htmlFor="conv-cor">Cor de identificação</Label>
                <div className="flex items-center gap-2">
                  <input id="conv-cor" type="color" value={edit.cor || '#0ea5e9'} onChange={(e) => setEdit({ ...edit, cor: e.target.value })} className="h-10 w-14 rounded border cursor-pointer" data-testid="conv-input-cor" />
                  <span className="text-xs font-mono text-muted-foreground">{edit.cor}</span>
                </div>
              </div>
              {campo('vigencia_inicio', 'Início da vigência', { type: 'date' })}
              {campo('vigencia_fim', 'Término da vigência', { type: 'date' })}
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="conv-observacoes">Observações administrativas</Label>
                <Input id="conv-observacoes" value={edit.observacoes ?? ''} onChange={(e) => setEdit({ ...edit, observacoes: e.target.value })} data-testid="conv-input-observacoes" />
              </div>
              <div className="flex items-center gap-3 sm:col-span-2">
                <Switch id="conv-ativo" checked={!!edit.ativo} onCheckedChange={(v) => setEdit({ ...edit, ativo: v })} data-testid="conv-switch-ativo" />
                <Label htmlFor="conv-ativo">Convênio ativo</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} data-testid="btn-cancelar-convenio">Cancelar</Button>
            <Button onClick={salvar} disabled={salvando || !edit?.codigo || !edit?.nome} data-testid="btn-salvar-convenio">{salvando ? 'Salvando…' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
